import { Link, router } from '@inertiajs/react';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { ChevronLeft, ChevronRight, Trash } from '@/components/ui/icons';
import { formatDate } from '@/lib/formatDate';
import { trackUmamiEvent } from '@/lib/analytics';
import type { Message } from '@/types';

type IndexProps = {
  messages: Message[];
  total: number;
  page: number;
  per: number;
};

const onDelete = (id: number, name: string) => {
  if (window.confirm(`Delete the message from ${name}?`)) {
    trackUmamiEvent('admin_message_delete', { source: 'index' });
    router.delete('/messages/' + id);
  }
};

const pagerButtonClassName =
  'border-line text-ink-soft hover:border-primary hover:text-primary-deep inline-flex min-h-[44px] items-center gap-1 rounded-sm border px-3.5 text-sm transition-colors disabled:pointer-events-none disabled:opacity-40';

export default function Index({ messages, total, page, per }: IndexProps) {
  const hasPrevious = page > 1;
  const hasNext = page * per < total;
  const totalPages = Math.max(1, Math.ceil(total / per));

  return (
    <div className="flex flex-col gap-7">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Eyebrow className="mb-2">Inbox</Eyebrow>
          <h1 className="font-display text-[26px] font-semibold sm:text-[30px]">Messages</h1>
        </div>
        <p className="text-ink-soft text-sm tabular-nums">
          {total} {total === 1 ? 'message' : 'messages'}
        </p>
      </div>

      {messages.length === 0 ? (
        <div className="border-line-soft text-ink-soft rounded border border-dashed px-6 py-16 text-center text-sm">
          No messages yet.
        </div>
      ) : (
        <>
          {/* Desktop/tablet: scannable table. */}
          <div className="border-line hidden overflow-hidden rounded border md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface text-ink-soft border-line-soft border-b text-xs tracking-wide uppercase">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Subject</th>
                  <th className="px-4 py-3 font-semibold">Received</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => (
                  <tr
                    key={message.id}
                    className="border-line-soft hover:bg-surface border-t transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Link
                        href={`/messages/${message.id}`}
                        className="text-ink hover:text-primary-deep font-medium"
                      >
                        {message.name}
                      </Link>
                    </td>
                    <td className="text-ink-soft px-4 py-3">{message.email}</td>
                    <td className="px-4 py-3">{message.subject}</td>
                    <td className="text-ink-soft px-4 py-3 tabular-nums">
                      {formatDate(message.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onDelete(message.id, message.name)}
                        aria-label={`Delete message from ${message.name}`}
                        className="text-error-fg hover:opacity-70"
                      >
                        <Trash className="size-[18px]" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: table collapses to stacked cards. */}
          <ul className="border-line divide-line-soft divide-y rounded border md:hidden">
            {messages.map((message) => (
              <li key={message.id}>
                <div className="flex items-start justify-between gap-3 p-4">
                  <Link href={`/messages/${message.id}`} className="min-w-0 flex-1">
                    <p className="text-ink truncate font-medium">{message.name}</p>
                    <p className="text-ink-soft truncate text-sm">{message.email}</p>
                    <p className="text-ink mt-1.5 truncate text-sm">{message.subject}</p>
                    <p className="text-ink-soft mt-1.5 text-xs tabular-nums">
                      {formatDate(message.createdAt)}
                    </p>
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(message.id, message.name)}
                    aria-label={`Delete message from ${message.name}`}
                    className="text-error-fg shrink-0 p-1 hover:opacity-70"
                  >
                    <Trash className="size-[18px]" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              disabled={!hasPrevious}
              onClick={() => router.get('/messages', { page: page - 1, per })}
              className={pagerButtonClassName}
            >
              <ChevronLeft className="size-4" />
              <span>Previous</span>
            </button>
            <span className="text-ink-soft text-sm tabular-nums">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={!hasNext}
              onClick={() => router.get('/messages', { page: page + 1, per })}
              className={pagerButtonClassName}
            >
              <span>Next</span>
              <ChevronRight className="size-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
